/**
 * CancelarCfdiNomina
 *
 * Modal para cancelar el CFDI de nómina ante el SAT. Sirve para los dos casos:
 *   - `lineaId` presente  → cancela ese recibo (respuesta inmediata).
 *   - `lineaId` en null   → cancela todos los recibos timbrados de la nómina,
 *                           en background con progreso por WebSocket.
 *
 * El aviso enumera lo que se revierte porque cancelar no es solo avisarle al
 * SAT: también deshace acumulados, saldos de créditos, ajuste al neto y mete la
 * reversa a la póliza. El folio de sustitución solo es obligatorio con motivo 01.
 */

import {
  message, Space, notification, Select, Alert } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  MensajeError,
  ModalItem,
  ButtonItem,
  Form,
  FormItem,
  InputItem,
  SelectItem,
  ErrorItem,
} from "../../../../Components/Items";

const { Option } = Select;

class CancelarCfdiNomina extends Component {
  formRef = React.createRef();
  state = { loading: false };

  onFinishFailed(e) {
    ErrorItem(e);
  }

  onFinish(values) {
    const { lineaId, prenominaId, empresaId, headersToken } = this.props;
    const esLote = !lineaId;

    this.setState({ loading: true });
    axios
      .post(
        esLote ? "prenomina/cancelarNominaCompleta" : "prenomina/cancelarCfdiNomina",
        esLote
          ? { ...values, prenominaId, empresaId }
          : { ...values, lineaId, empresaId },
        { headers: headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });

        if (res.data.success === 2) {
          // Limpiar antes de avisarle al padre: el callback cierra el modal y
          // deja de haber formRef al que resetear.
          this.formRef.current?.resetFields();

          if (esLote) {
            // El backend responde de inmediato y el avance llega por WebSocket.
            this.props.onCancelacionIniciada(res.data.total);
          } else {
            message.success("CFDI cancelado");
            if (res.data.advertencias?.length) {
              notification.warning({
                message: "Cancelado con advertencias",
                description: res.data.advertencias.join(" "),
                duration: null,
              });
            }
            this.props.onCancelado();
          }
        } else if (res.data.success === 1) {
          MensajeError({
            message: res.data.errorTimbrado?.titulo || "No se pudo cancelar el recibo",
            description: res.data.errorTimbrado?.mensaje || res.data.message,
            detalleTecnico: res.data.errorTimbrado?.detalleTecnico,
          });
        } else {
          message.error("Sucedió un error y no se pudo cancelar");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Sucedió un error y no se pudo cancelar");
      });
  }

  regresar = () => {
    this.formRef.current?.resetFields();
    this.props.cerrar();
  };

  render() {
    const { visible, lineaId, nombreEmpleado, totalTimbradas, esFiniquito } = this.props;
    const esLote = !lineaId;

    return (
      <ModalItem
        title={esLote ? "Cancelar Nómina Completa" : "Cancelar CFDI de Nómina"}
        visible={visible}
        spinning={this.state.loading}
        onCancel={this.regresar}
        paddingTop
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            esLote
              ? `Se cancelarán ante el SAT los ${totalTimbradas} recibos timbrados de esta nómina.`
              : `Se cancelará ante el SAT el recibo de ${nombreEmpleado || "este empleado"}.`
          }
          description={
            <span style={{ fontSize: 12 }}>
              Además del CFDI se revierten los acumulados del ejercicio, los saldos de
              créditos INFONAVIT/FONACOT y movimientos permanentes, y el saldo de ajuste al
              neto.{" "}
              {/* La contabilidad se deshace distinto según el caso, y decir el que no es deja
                  al contador buscando renglones que no existen. La póliza de un finiquito es
                  suya y de nadie más, así que se puede cancelar entera; la de la nómina se
                  comparte entre empleados y por eso sólo recibe los renglones espejo. */}
              {esFiniquito
                ? "Su póliza contable se cancela completa, con todos sus renglones; al volver a timbrar se genera una póliza nueva."
                : "La póliza recibe los movimientos espejo que la dejan en cero."}{" "}
              El timbre consumido no se devuelve. {esLote ? "Los recibos" : "El recibo"} vuelve
              a quedar editable para corregirlo y timbrarlo de nuevo.
            </span>
          }
        />

        <Form
          ref={this.formRef}
          name="cancelarCfdiNomina"
          layout
          width={700}
          onFinish={this.onFinish.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <FormItem label="Motivo" name="motivo" required margin>
            <SelectItem placeholder="Motivo">
              <Option value="01">01 - Comprobante emitido con errores con relación.</Option>
              <Option value="02">02 - Comprobante emitido con errores sin relación.</Option>
              <Option value="03">03 - No se llevó a cabo la operación.</Option>
              <Option value="04">04 - Operación nominativa relacionada en una factura global.</Option>
            </SelectItem>
          </FormItem>
          <FormItem noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <FormItem
                label="Folio Sustitución"
                name="folioSustitucion"
                margin
                max={200}
                required={getFieldValue("motivo") === "01"}
                tooltip="UUID del CFDI que sustituye al cancelado. Solo lo pide el SAT con el motivo 01."
              >
                <InputItem placeholder="Folio de Sustitución" />
              </FormItem>
            )}
          </FormItem>

          <div style={{ display: "flex", justifyContent: "right", marginTop: 20, marginBottom: 10 }}>
            <Space>
              <ButtonItem type="primary" danger htmlType="submit">
                {esLote ? `Cancelar ${totalTimbradas} CFDIs` : "Cancelar CFDI"}
              </ButtonItem>
              <ButtonItem type="primary" onClick={this.regresar}>
                Regresar
              </ButtonItem>
            </Space>
          </div>
        </Form>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(CancelarCfdiNomina);
